import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

import { Button, Space } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import { getTableScrollX } from '@/utils/table';

import VehiclesHistoryExtraFilters from '@/components/History/VehiclesHistory/VehiclesHistoryExtraFilters';
import {
  VehicleHistory,
  VehicleHistoryQuery,
} from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.types';
import { useVehiclesHistory } from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.hooks';
import { ColumnsType } from 'antd/lib/table';
import { DefaultRouteTabs, InfiniteTable } from '@/components/_common';
import EyeIcon from '@/components/_common/EyeIcon';
import { applyMask } from '@/utils/mask';
import { useDebounce } from '@/hooks';
import { removePlateMask } from '@/utils/mask/removeMask';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { vehicleHistoryRoutes } from './route';
import { isAppPortocar } from '@/index';

function VehiclesHistoryPage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<VehicleHistoryQuery>({
    order: 'ASC',
    ...convertParams<VehicleHistoryQuery>({
      search: 'string',
      custumerFilter: 'string',
      licensePlateFilter: 'string',
      vehicleFilter: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useVehiclesHistory(queryDebounce);

  const vehiclesHistory = flatInfiniteData(data);

  const columns: ColumnsType<VehicleHistory> = [
    {
      dataIndex: 'customer',
      key: 'customer',
      title: intl.formatMessage({ id: 'vehiclesHistory.list.header.customer' }),
      ellipsis: true,
      width: 220,
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      title: intl.formatMessage({
        id: 'vehiclesHistory.list.column.licensePlate',
      }),
      ellipsis: true,
      width: 220,
      render: (value: string) => {
        const maskLicensePlate = [
          /\d/,
          /\d/,
          /\d/,
          '-',
          /\d/,
          /\d/,
          /\d/,
          /\d/,
        ];

        return applyMask(value, maskLicensePlate);
      },
    },
    {
      dataIndex: 'fleet',
      key: 'fleet',
      title: intl.formatMessage({
        id: 'vehiclesHistory.list.column.fleet',
      }),
      ellipsis: true,
      width: 220,
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: 'vehicle',
      key: 'vehicle',
      title: intl.formatMessage({ id: 'vehiclesHistory.list.column.vehicle' }),
      ellipsis: true,
      width: 140,
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      title: intl.formatMessage({ id: 'vehiclesHistory.list.header.actions' }),
      ellipsis: true,
      render: (_, record) => (
        <Space>
          <EyeIcon
            redirect={`/app/history/vehiclesHistory/${record.licensePlate}/detail`}
          />
        </Space>
      ),
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  const newRoute = {
    ...route.parent,
    routes: route.parent.routes.filter((route) => {
      const isAllRoute = vehicleHistoryRoutes.includes(route.key);
      if (route.key === 'plateHistory') return isAllRoute && isAppPortocar;
      return isAllRoute;
    }),
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs persistSearchParams={false} route={newRoute}>
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              search: removePlateMask(value),
            }))
          }
          searchValue={queryParams.search}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() => setQueryParams({ order: 'ASC' })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <VehiclesHistoryExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<VehicleHistory>
          rowKey={(row) => row.licensePlate}
          columns={columns}
          dataSource={vehiclesHistory}
          loading={isFetching}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </DefaultRouteTabs>
    </>
  );
}

export default VehiclesHistoryPage;
